import React from "react";
import { observer } from "mobx-react-lite";

import pl from "shared/messages/pl";
import { constants } from "shared/constants";
import { formatDate, formatDateYearOnly, formatNumber } from "utils/formattingUtils";
import { BasicTable, RegularText, TextTableHeader } from "shared/components";
import { Training } from "view360Card/models/Training";
import { MobileViewCard } from "../mobileViewCard/MobileViewCard";
import { mapBoolValues } from "utils/entityUtils";
import { MobileViewCardDetailsItem } from "../mobileViewCardDetailsItem/MobileViewCardDetailsItem";
import styles from "./trainingsTable.module.scss";

export interface TrainingsTableProps {
    trainings: Training[];
}

export const TrainingsTable: React.FC<TrainingsTableProps> = observer((props) => {
    const headerItems = [
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.date} />,
            key: "date",
            className: styles.columnTight,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.name} />,
            key: "name",
            className: styles.columnTrainingName,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.tenantCode} />,
            key: "tennantCode",
            className: styles.columnTight,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.type} />,
            key: "description",
            className: styles.columnTrainingType,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.workTraining} />,
            key: "workTraining",
            className: styles.columnTight,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.completed} />,
            key: "completedFlag",
            className: styles.columnTight,
        },
        {
            content: <TextTableHeader content={pl.view360Card.generalInformations.salesPerson.training.hoursCount} />,
            key: "hours",
            className: styles.columnTight,
        },
    ];

    const getRows = () => {
        return props.trainings.map((training) => {
            return {
                key: training.id,
                items: [
                    {
                        key: `${training.id}-date`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={`${
                                    training.trainingDate
                                        ? training.isSummary
                                            ? formatDateYearOnly(training.trainingDate)
                                            : formatDate(training.trainingDate)
                                        : constants.noValue
                                }`}
                            />
                        ),
                        className: styles.columnTight,
                    },
                    {
                        key: `${training.id}-name`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={training.trainingName ?? constants.noValue}
                            />
                        ),
                        className: styles.columnTrainingName,
                    },
                    {
                        key: `${training.id}-tennantCode`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={training.tenantCode ?? constants.noValue}
                            />
                        ),
                        className: styles.columnTight,
                    },
                    {
                        key: `${training.id}-description`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={training.type?.description ?? constants.noValue}
                            />
                        ),
                        className: styles.columnTrainingType,
                    },
                    {
                        key: `${training.id}-workTraining`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={mapBoolValues(training.workTraining)}
                            />
                        ),
                        className: styles.columnTight,
                    },
                    {
                        key: `${training.id}-completedFlag`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={mapBoolValues(training.completionFlag)}
                            />
                        ),
                        className: styles.columnTight,
                    },
                    {
                        key: `${training.id}-hours`,
                        content: (
                            <RegularText
                                weight={training.isSummary ? "semibold" : undefined}
                                content={training.hoursCount ? formatNumber(training.hoursCount) : constants.noValue}
                            />
                        ),
                        className: styles.columnTightAlignRight,
                    },
                ],
            };
        });
    };

    const getMobilViewDetailItem = (training: Training) => {
        return [
            <MobileViewCardDetailsItem
                label={`${
                    training.isSummary
                        ? pl.view360Card.generalInformations.salesPerson.training.year
                        : pl.view360Card.generalInformations.salesPerson.training.date
                } ${constants.inlineSeparator} ${pl.view360Card.generalInformations.salesPerson.training.tenantCode}`}
                content={`${
                    training.trainingDate
                        ? training.isSummary
                            ? formatDateYearOnly(training.trainingDate)
                            : formatDate(training.trainingDate)
                        : constants.noValue
                } ${constants.inlineSeparator} ${training.tenantCode ?? constants.noValue}`}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.generalInformations.salesPerson.training.type} ${constants.inlineSeparator} ${pl.view360Card.generalInformations.salesPerson.training.hoursCount}`}
                content={`${training.type?.description ?? constants.noValue} ${constants.inlineSeparator} ${
                    training.hoursCount ? formatNumber(training.hoursCount) : constants.noValue
                }`}
            />,
            <MobileViewCardDetailsItem
                label={`${pl.view360Card.generalInformations.salesPerson.training.workTraining} ${constants.inlineSeparator} ${pl.view360Card.generalInformations.salesPerson.training.completed}`}
                content={`${mapBoolValues(training.workTraining)} ${constants.inlineSeparator} ${mapBoolValues(
                    training.completionFlag
                )}`}
            />,
        ];
    };

    const getMobileViewRows = () => {
        if (!props.trainings) {
            return null;
        }

        return props.trainings.map((training) => {
            return (
                <MobileViewCard
                    key={training.id}
                    header={training.trainingName ?? constants.noValue}
                    detailsItems={getMobilViewDetailItem(training)}
                />
            );
        });
    };

    return (
        <BasicTable
            rows={getRows()}
            headerItems={headerItems}
            mobileViewRows={getMobileViewRows()}
            maxDefaultSize={constants.tableMaxDefaultSize}
        />
    );
});
